import React, { useRef, useMemo } from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import {
  ScreenLink,
  useScreenDispatch,
  useScreenState,
  useScreenTitle,
} from '@gogame/screens';
import { Link, Stack, Typography } from '@gogame/components';

import AudienceDetails from 'scenes/CustomAudiences/Audiences/components/AudienceDetails';
import {
  LookalikeAudiencePartnerAudienceBasedFormCreate,
  PARTNER,
  PARTNER_AUDIENCE,
} from 'scenes/CustomAudiences/LookalikeAudiences/components/LookalikeAudienceForm';
import { PartnerAudienceFormEdit } from 'scenes/CustomAudiences/PartnerAudiences/components/PartnerAudienceForm';
import ShareAccountsForm, {
  PARTNER_AUDIENCE as SHARE_ACCOUNT_PARTNER_AUDIENCE,
} from 'scenes/CustomAudiences/PartnerAudiences/components/ShareAccountsForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_FILTERSTATUS,
  COLUMN_PARENT_AUDIENCE,
  COLUMN_PARTNER,
  COLUMN_PARTNER_API_ID,
  COLUMN_DATA_TYPE,
  COLUMN_SIZE,
  COLUMN_LAST_SYNC,
  COLUMN_ACCOUNT,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
  COLUMN_OWNER,
  COLUMN_LAST_SYNC_FAIL_DATE,
  COLUMN_LAST_FAILS_COUNT,
} from 'scenes/CustomAudiences/PartnerAudiences/constants';
import {
  useLoadPAGrid,
  useIsUpdating,
} from 'scenes/CustomAudiences/PartnerAudiences/data';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/CustomAudiences/PartnerAudiences/screenReducer';
import { partnerAudiencesDetailsLink } from 'scenes/CustomAudiences/PartnerAudiences/navigation';
import { getColor } from 'scenes/CustomAudiences/PartnerAudiences/components/Status';
import PartnerAudienceActions from 'scenes/CustomAudiences/PartnerAudiences/components/Actions';

import Toolbar from './Toolbar';
import BatchToolbar from './BatchToolbar';

const AudiencesDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const [childComponent, openChildComponent] = useChildComponent();

  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const { filters, tableSettings, searchQuery } = screenState;

  const { data, isLoading, isRefetching } = useLoadPAGrid({
    filters,
    search: searchQuery,
    ...tableSettings,
  });

  const isUpdating = useIsUpdating();

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleOpenAudience = (_e, { id }) => {
    openChildComponent(AudienceDetails, { id });
  };

  const handleShare = async ({ id }) => {
    openChildComponent(ShareAccountsForm, {
      initialValues: {
        [SHARE_ACCOUNT_PARTNER_AUDIENCE]: id,
      },
    });
  };
  const handleEdit = async ({ id }) => {
    openChildComponent(PartnerAudienceFormEdit, {
      id,
    });
  };

  const handleAddLookalikeAudiences = async ({ id, partner }) => {
    openChildComponent(LookalikeAudiencePartnerAudienceBasedFormCreate, {
      initialValues: { [PARTNER_AUDIENCE]: id, [PARTNER]: partner.id },
    });
  };
  const apiRef = useRef();

  const sortedOrder = [...screenState.columns.order].sort();
  const visibleColumns = useMemo(() => sortedOrder, [sortedOrder.toString()]);

  return (
    <React.Fragment>
      <DataGrid
        apiRef={apiRef}
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading || isUpdating}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        visibleColumns={visibleColumns}
        storageKey={'partnerAudiences'}
        defaultColumnWidth={160}
        checkboxSelection={true}
        toolbar={<Toolbar />}
        selectedRowsToolbarActions={({ selectedValues }) => (
          <BatchToolbar
            selectedValues={selectedValues}
            openChildComponent={openChildComponent}
          />
        )}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'text'}
          headerName={'Name'}
          width={322}
          fixed={true}
          resizable={false}
          formatter={(value, { id }) => (
            <ScreenLink link={partnerAudiencesDetailsLink} pathParams={{ id }}>
              {value}
            </ScreenLink>
          )}
        />
        <DataGrid.Column
          headerName={'Status'}
          field={COLUMN_FILTERSTATUS}
          width={110}
          type={'status'}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_SIZE}
          type={'number'}
          headerName={'Size'}
          width={104}
        />
        <DataGrid.Column
          field={COLUMN_PARENT_AUDIENCE}
          headerName={'GG Audience'}
          width={230}
          sortable={false}
          formatter={(value) => (
            <Stack zeroMinWidth={true}>
              <Link onClick={(e) => handleOpenAudience(e, { id: value?.id })}>
                <Typography variant={'body2'} noWrap={true}>
                  {value?.name}
                </Typography>
              </Link>
            </Stack>
          )}
        />
        <DataGrid.Column
          field={COLUMN_PARTNER}
          type={'text'}
          headerName={'Partner'}
          width={128}
          getValue={(value) => value?.name}
        />
        <DataGrid.Column
          field={COLUMN_PARTNER_API_ID}
          type={'text'}
          headerName={'Partner List ID'}
          width={187}
        />
        <DataGrid.Column
          field={COLUMN_DATA_TYPE}
          type={'text'}
          headerName={'Data Type'}
          width={160}
          getValue={(value) => value?.name}
        />
        <DataGrid.Column
          field={COLUMN_ACCOUNT}
          headerName={'Account'}
          width={172}
          formatter={(value) =>
            value?.id ? (
              <Stack zeroMinWidth={true}>
                <Typography variant={'body2'} noWrap={true}>
                  {value.name}
                </Typography>
                <Typography variant={'body2'} noWrap={true}>
                  ID {value.id}
                </Typography>
              </Stack>
            ) : null
          }
        />
        <DataGrid.Column
          field={COLUMN_LAST_SYNC}
          headerName={'Last Sync'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_OWNER}
          type={'text'}
          headerName={'Owner'}
          width={166}
          sortable={false}
          getValue={(value) => value?.name}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          type={'text'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_SYNC_FAIL_DATE}
          headerName={'Last Sync Fail Time'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_LAST_FAILS_COUNT}
          headerName={'Number of Sync Fails'}
          type={'text'}
          sortable={false}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          limit={3}
          onShare={handleShare}
          onEdit={handleEdit}
          onAddLookalikeAudiences={handleAddLookalikeAudiences}
          actionsComponent={PartnerAudienceActions}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default AudiencesDataTable;
